// @workflow assistant.voice
import { IDLE_READING, READ_ACTION as A, nextReading, isReading, canReadAloud } from '@Assistant/domain/readAloudState';
import { READ_ALOUD_TEXT, readAloudFailure } from '@Assistant/domain/readAloudMessages';
import { readAloudPlayer } from '@Assistant/services/readAloud';
import { showToast } from '@Assistant/services/toast';

/**
 * Read aloud in the ASSISTANT store: the one reply loading or playing across the whole chat.
 * `readAloudNow` and `readAloudProgress` are kept apart (domain/readAloudState.js), and each is
 * committed only when it changed. The playing session is kept outside state, like the agent run.
 *
 * Stops when: another reply starts, the mic turns on (SET_DICTATING), a new or another
 * conversation opens (NEW_CONVERSATION), AVA closes (CLOSE / TOGGLE), the reply's button goes
 * away, or AvaAssistant unmounts. Nothing starts while a mic is on. A new or another
 * conversation and the unmount also forget the audio kept in memory (readAloudPlayer.js).
 */

let active = null;
let sessions = 0;

const readingOf = (state) => ({ now: state.readAloudNow, progress: state.readAloudProgress });

function apply({ commit, state }, action) {
  const before = readingOf(state);
  const next = nextReading(before, action);
  if (next.now !== before.now) commit('SET_READ_ALOUD_NOW', next.now);
  if (next.progress !== before.progress) commit('SET_READ_ALOUD_PROGRESS', next.progress);
}

function stopActive(context) {
  if (active) active.stop();
  active = null;
  apply(context, { type: A.STOP });
}

export const READ_ALOUD_STATE = () => ({ readAloudNow: IDLE_READING.now, readAloudProgress: IDLE_READING.progress });

export const READ_ALOUD_GETTERS = {
  READ_ALOUD_NOW: (s) => s.readAloudNow,
  READ_ALOUD: (s) => readingOf(s),
  /** Whether a reply shows Read aloud (a finished reply the server has stored). */
  CAN_READ_ALOUD: () => canReadAloud,
};

export const READ_ALOUD_MUTATIONS = {
  SET_READ_ALOUD_NOW(state, now) { state.readAloudNow = now; },
  SET_READ_ALOUD_PROGRESS(state, progress) { state.readAloudProgress = progress; },
};

export const READ_ALOUD_ACTIONS = {
  /** Reads one reply aloud; any other reply being read stops first. */
  READ_ALOUD(context, messageId) {
    const { state, dispatch } = context;
    if (state.isDictating || !messageId) return;
    if (active) active.stop();
    active = null;
    sessions += 1;
    const session = sessions;
    apply(context, { type: A.START, messageId, session });
    active = { session, stop: () => {} };
    // Started synchronously, inside the click, so the browser lets it play.
    const handle = readAloudPlayer.start({
      conversationId: state.conversation.id,
      messageId,
      onEvent: (event) => {
        if (!active || active.session !== session) return;
        apply(context, { ...event, session });
        if (event.type !== A.ENDED && event.type !== A.FAILED) return;
        active = null;
        if (event.type === A.FAILED) {
          const failure = readAloudFailure(event.code);
          showToast(failure.text, failure.retry ? { action: { label: READ_ALOUD_TEXT.TRY_AGAIN, run: () => dispatch('READ_ALOUD', messageId) } } : {});
        }
      },
    });
    if (active && active.session === session) active = { session, stop: handle.stop };
    else handle.stop(); // ended or stopped before start() returned
  },
  STOP_READING(context) {
    stopActive(context);
  },
  /** Stops, and forgets the audio kept for a second play (a new or another chat, AVA unmounting). */
  FORGET_READ_ALOUD(context) {
    stopActive(context);
    readAloudPlayer.clear();
  },
  /** The reply's button: Read aloud, or Stop while that reply loads or plays. */
  TOGGLE_READ_ALOUD(context, messageId) {
    if (isReading(context.state.readAloudNow, messageId)) stopActive(context);
    else context.dispatch('READ_ALOUD', messageId);
  },
};
